import { createContext, PropsWithChildren, useContext, useState } from "react";

type ModalState = {
    open: boolean,
    value: any,
    closeable: boolean
}

type ModalStateMap = {
    [code: string]: ModalState
}

interface ModalContextReturn {
    openModal: (code: string, value?: any) => void,
    closeModal: (code: string) => void,
    isModalOpen: (code: string) => boolean,
    getModalValue: (code: string) => any,
    isModalCloseable: (code: string) => boolean,
    setModalCloseable: (code: string, value: boolean) => void
}

const ModalHandlerContext = createContext<ModalContextReturn>({
    openModal: () => { },
    closeModal: () => { },
    isModalOpen: () => false,
    getModalValue: () => { },
    isModalCloseable: () => false,
    setModalCloseable: () => { }
});

export const useModalHandlerContext = () => {
    return useContext(ModalHandlerContext);
}

export const ModalHandlerProvider = ({
    children
}: PropsWithChildren) => {
    const [modalState, setModalState] = useState<ModalStateMap>({});

    const openModal = (code: string, value: any = "") => {
        setModalState(prev => ({ ...prev, [code]: { open: true, value, closeable: true } }))
    }

    const closeModal = (code: string) => {
        setModalState(prev => {
            const modal = prev[code];
            if (!modal) return prev;
            if (!modal.closeable) return prev;
            return { ...prev, [code]: { ...modal, open: false } }
        })
    }

    const isModalOpen = (code: string) => {
        const modal = modalState[code];
        if (!modal) return false;
        return modal.open;
    }

    const getModalValue = (code: string) => {
        const modal = modalState[code];
        if (!modal) return;
        return modal.value;
    }

    const isModalCloseable = (code: string) => {
        const modal = modalState[code];
        if (!modal) return false;
        return modal.open;
    }

    const setModalCloseable = (code: string, value: boolean) => {
        setModalState(prev => {
            const modal = prev[code];
            if (!modal) return prev;
            return { ...prev, [code]: { ...modal, closeable: value } }
        })
    }

    return <ModalHandlerContext.Provider value={{
        openModal,
        closeModal,
        isModalOpen,
        getModalValue,
        isModalCloseable,
        setModalCloseable
    }}>
        {children}
    </ModalHandlerContext.Provider>
}